<template>
                <div v-if="currentView === 'server_mail'">
                    <div class="page-header"><div><div class="page-title">郵件服務管理</div><div class="page-subtitle">Postfix / Dovecot 服務狀態與設定</div></div></div>

                    <div class="mb-4 border-b">
                        <button @click="mailConfigTab = 'status'; fetchServiceStatus()"
                            :style="mailConfigTab === 'status' ? 'border-bottom:2px solid var(--primary);font-weight:700;' : ''"
                            class="px-4 py-2">服務狀態</button>
                        <button @click="mailConfigTab = 'basic'; fetchMailSettings()"
                            :style="mailConfigTab === 'basic' ? 'border-bottom:2px solid var(--primary);font-weight:700;' : ''"
                            class="px-4 py-2">基本設定 (SMTP)</button>
                        <button @click="mailConfigTab = 'dovecot_basic'; fetchDovecotSettings()"
                            :style="mailConfigTab === 'dovecot_basic' ? 'border-bottom:2px solid var(--primary);font-weight:700;' : ''"
                            class="px-4 py-2">IMAP/POP3 設定</button>
                        <button @click="mailConfigTab = 'database'; fetchDbStats()"
                            :style="mailConfigTab === 'database' ? 'border-bottom:2px solid var(--primary);font-weight:700;' : ''"
                            class="px-4 py-2">資料庫管理</button>
                        <button @click="mailConfigTab = 'ai_service'; fetchAiSettings()"
                            :style="mailConfigTab === 'ai_service' ? 'border-bottom:2px solid var(--primary);font-weight:700;' : ''"
                            class="px-4 py-2">AI 服務設定</button>
                    </div>

                    <div v-if="mailConfigTab === 'status'" class="card p-6">
                        <h3 class="card-title mb-4">服務狀態與控制</h3>
                        <div class="space-y-4">
                            <div v-for="(status, service) in serviceStatus" :key="service"
                                class="flex justify-between items-center border-b py-4">
                                <div>
                                    <span class="capitalize font-bold text-lg block">{{ service }}</span>
                                    <span class="text-sm" style="color:var(--gray-500);">
                                        {{ service === 'postfix' ? 'SMTP Server' : (service === 'dovecot' ? 'IMAP/POP3 Server' : (service === 'database' ? 'Database' : (service === 'webmail' ? 'Webmail Client' : 'AI Analysis'))) }}
                                    </span>
                                    <a v-if="service === 'webmail' && status === 'running'" href="http://localhost:8081"
                                        target="_blank" class="text-xs hover:underline ml-2" style="color:var(--primary);">開啟
                                        Webmail</a>
                                </div>
                                <div class="flex items-center gap-4">
                                    <span class="px-3 py-1 rounded text-sm font-bold text-white uppercase"
                                        :style="status === 'running' ? 'background:var(--success);' : (status === 'stopped' || status === 'exited' ? 'background:var(--danger);' : 'background:var(--gray-500);')">
                                        {{ status }}
                                    </span>
                                    <div class="flex gap-2">
                                        <button @click="controlService(service, 'start')"
                                            :disabled="status === 'running'"
                                            class="btn btn-success btn-xs">啟動</button>
                                        <button @click="controlService(service, 'stop')"
                                            :disabled="status !== 'running'"
                                            class="btn btn-danger btn-xs">停止</button>
                                        <button @click="controlService(service, 'restart')"
                                            class="btn btn-warning btn-xs">重啟</button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div v-if="mailConfigTab === 'basic'" class="card p-6">
                        <h3 class="card-title mb-4">常用郵件設定</h3>
                        <div class="grid grid-cols-1 gap-6 max-w-4xl">
                            <div>
                                <label class="block text-sm font-bold mb-1">主機名稱 (myhostname)</label>
                                <div class="flex gap-2">
                                    <input v-model="mailSettings.myhostname" type="text"
                                        :disabled="!isSuperAdmin" class="flex-1 border p-2 rounded"
                                        placeholder="mail.example.com">
                                    <select @change="mailSettings.myhostname = $event.target.value"
                                        :disabled="!isSuperAdmin" class="input">
                                        <option value="">從網域選擇...</option>
                                        <option v-for="(d, idx) in safeArr(domains)" :key="keyById(d, 'domain-opt5', idx)" :value="d.name">{{ d.name }}</option>
                                    </select>
                                </div>
                                <p class="text-xs mt-1" style="color:var(--gray-500);">SMTP 伺服器的主機名稱。</p>
                            </div>

                            <div>
                                <label class="block text-sm font-bold mb-1">SMTP 歡迎詞 (smtpd_banner)</label>
                                <input v-model="mailSettings.smtpd_banner" type="text" :disabled="!isSuperAdmin"
                                    class="w-full border p-2 rounded" placeholder="$myhostname ESMTP $mail_name">
                                <p class="text-xs mt-1" style="color:var(--gray-500);">連線時顯示的歡迎訊息。</p>
                            </div>

                            <div class="grid grid-cols-2 gap-4">
                                <div>
                                    <label class="block text-sm font-bold mb-1">信件最大容量 (bytes)</label>
                                    <input v-model="mailSettings.message_size_limit" type="number"
                                        :disabled="!isSuperAdmin" class="w-full border p-2 rounded">
                                    <p class="text-xs mt-1" style="color:var(--gray-500);">預設 10240000 (10MB)。</p>
                                </div>
                                <div>
                                    <label class="block text-sm font-bold mb-1">單封信件最多收件人</label>
                                    <input v-model="mailSettings.smtpd_recipient_limit" type="number"
                                        :disabled="!isSuperAdmin" class="w-full border p-2 rounded">
                                    <p class="text-xs mt-1" style="color:var(--gray-500);">限制單次傳送的收件人數。</p>
                                </div>
                            </div>

                            <div>
                                <label class="block text-sm font-bold mb-1">允許 Relay 的網路 (mynetworks)</label>
                                <textarea v-model="mailSettings.mynetworks"
                                    :disabled="!isSuperAdmin"
                                    class="w-full border p-2 rounded h-24 font-mono text-sm"></textarea>
                                <p class="text-xs mt-1" style="color:var(--gray-500);">允許進行郵件轉發的 IP 範圍 (CIDR 格式)，以逗號或空格分隔。</p>
                            </div>

                            <!-- D1: Force TLS -->
                            <div class="rounded-lg p-4" style="background:var(--gray-50);border:1px solid var(--border);">
                                <div class="flex items-center justify-between">
                                    <div>
                                        <div class="font-semibold text-sm" style="color:var(--gray-800);">強制 TLS 加密傳送 (smtp_tls_security_level)</div>
                                        <div class="text-xs mt-1" style="color:var(--gray-500);">
                                            開啟後：對端 MTA 必須支援 TLS，否則退信（encrypt 模式）<br>
                                            關閉時：嘗試 TLS，不支援則明文傳送（may 模式）
                                        </div>
                                    </div>
                                    <button
                                        v-if="isSuperAdmin"
                                        @click="mailSettings.force_tls = !mailSettings.force_tls"
                                        class="relative inline-flex items-center rounded-full transition-colors flex-shrink-0 ml-4"
                                        :style="{ width: '44px', height: '24px', background: mailSettings.force_tls ? 'var(--brand-500)' : 'var(--gray-300)' }"
                                    >
                                        <span class="inline-block rounded-full transition-transform" style="background:#fff;"
                                            :style="{ width: '18px', height: '18px', transform: mailSettings.force_tls ? 'translateX(23px)' : 'translateX(3px)', boxShadow: 'var(--shadow-sm)' }"></span>
                                    </button>
                                    <span v-else class="badge" :class="mailSettings.force_tls ? 'badge-success' : ''">{{ mailSettings.force_tls ? 'encrypt' : 'may' }}</span>
                                </div>
                                <div v-if="mailSettings.force_tls" class="mt-2 text-xs p-2 rounded" style="background:var(--color-warning-bg);color:var(--color-warning);">
                                    ⚠️ 強制 TLS 可能導致向不支援 TLS 的對端退信，請確認對端支援度後再啟用。
                                </div>
                            </div>

                            <div class="pt-4 border-t">
                                <button v-if="isSuperAdmin" @click="saveMailSettings"
                                    class="btn btn-primary">儲存設定</button>
                                <p v-if="isSuperAdmin" class="text-sm mt-2 inline-block ml-4" style="color:var(--danger);">注意：儲存後需手動重啟 Postfix 服務生效。</p>
                                <div v-else class="text-xs mt-2" style="color:var(--gray-500);">僅超級管理員可修改此區設定。</div>
                            </div>
                        </div>
                    </div>

                    <div v-if="mailConfigTab === 'dovecot_basic'" class="card p-6">
                        <h3 class="card-title mb-4">IMAP/POP3 常用設定 (Dovecot)</h3>
                        <div class="grid grid-cols-1 gap-6 max-w-4xl">
                            <div>
                                <label class="block text-sm font-bold mb-1">啟用協定 (protocols)</label>
                                <input v-model="dovecotSettings.protocols" type="text" :disabled="!isSuperAdmin"
                                    class="w-full border p-2 rounded" placeholder="imap pop3 lmtp sieve">
                                <p class="text-xs mt-1" style="color:var(--gray-500);">例如: imap pop3 lmtp sieve</p>
                            </div>
                            <div>
                                <label class="block text-sm font-bold mb-1">監聽介面 (listen)</label>
                                <input v-model="dovecotSettings.listen" type="text" :disabled="!isSuperAdmin"
                                    class="w-full border p-2 rounded" placeholder="*">
                                <p class="text-xs mt-1" style="color:var(--gray-500);">* 代表監聽所有 IPv4 介面，[::] 代表 IPv6。</p>
                            </div>
                            <div>
                                <label class="block text-sm font-bold mb-1">禁止明文驗證 (disable_plaintext_auth)</label>
                                <select v-model="dovecotSettings.disable_plaintext_auth"
                                    :disabled="!isSuperAdmin"
                                    class="input">
                                    <option value="yes">是 (建議)</option>
                                    <option value="no">否</option>
                                </select>
                                <p class="text-xs mt-1" style="color:var(--gray-500);">強制使用加密連線進行驗證。</p>
                            </div>
                            <div>
                                <label class="block text-sm font-bold mb-1">啟用 SSL (ssl)</label>
                                <select v-model="dovecotSettings.ssl" :disabled="!isSuperAdmin"
                                    class="input">
                                    <option value="yes">是 (必要)</option>
                                    <option value="required">強制 (Required)</option>
                                    <option value="no">否</option>
                                </select>
                            </div>
                            <div class="pt-4 border-t">
                                <button v-if="isSuperAdmin" @click="saveDovecotSettings"
                                    class="btn btn-primary">儲存設定</button>
                                <p v-if="isSuperAdmin" class="text-sm mt-2 inline-block ml-4" style="color:var(--color-danger);">注意：儲存後需手動重啟 Dovecot 服務生效。</p>
                                <div v-else class="text-xs mt-2" style="color:var(--gray-500);">僅超級管理員可修改此區設定。</div>
                            </div>
                        </div>
                    </div>

                    <div v-if="mailConfigTab === 'database'" class="card p-6">
                        <h3 class="card-title mb-4">資料庫統計 (MariaDB)</h3>
                        <div v-if="dbStats" class="grid grid-cols-2 gap-6 max-w-4xl">
                            <div class="card p-4">
                                <div class="text-sm" style="color:var(--gray-500);">版本</div>
                                <div class="text-xl font-bold">{{ dbStats.version }}</div>
                            </div>
                            <div class="card p-4">
                                <div class="text-sm" style="color:var(--gray-500);">資料庫大小</div>
                                <div class="text-xl font-bold">{{ dbStats.size_mb.toFixed(2) }} MB</div>
                            </div>
                            <div class="col-span-2">
                                <h4 class="font-bold mb-2">資料表記錄數</h4>
                                <div class="grid grid-cols-3 gap-4">
                                    <div v-for="(count, table) in dbStats.tables"
                                        class="p-3 border rounded flex justify-between">
                                        <span class="capitalize">{{ table }}</span>
                                        <span class="font-mono font-bold">{{ count }}</span>
                                    </div>
                                </div>
                            </div>
                            <div class="col-span-2 pt-4 border-t">
                                <button @click="fetchDbStats"
                                    class="btn btn-dark">重新整理</button>
                            </div>
                        </div>
                        <div v-else style="color:var(--gray-500);">載入中...</div>
                    </div>

                    <div v-if="mailConfigTab === 'ai_service'" class="card p-6">
                        <h3 class="card-title mb-4">AI 服務設定</h3>
                        <div class="max-w-4xl">
                            <div class="mb-4 p-4 rounded" style="background:var(--gray-50);border:1px solid var(--border);">
                                <div class="font-bold mb-3" style="color:var(--gray-700);">CBI 官網最小抓取</div>
                                <div class="mb-4">
                                    <label class="block text-sm font-bold mb-2" style="color:var(--gray-700);">啟用官網抓取</label>
                                    <div class="flex gap-2 items-center h-10">
                                        <label class="inline-flex items-center cursor-pointer">
                                            <input type="checkbox" v-model="aiSettingsForm.cbi_crawl_enabled"
                                                class="sr-only peer"
                                                @change="saveAiSetting('cbi_crawl_enabled', aiSettingsForm.cbi_crawl_enabled)">
                                            <div
                                                class="relative w-11 h-6 rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:border after:rounded-full after:h-5 after:w-5 after:transition-all" :style="aiSettingsForm.cbi_crawl_enabled ? 'background:var(--success);' : 'background:var(--gray-200);'">
                                            </div>
                                            <span class="ms-3 text-sm font-medium" style="color:var(--gray-900);">{{
                                                aiSettingsForm.cbi_crawl_enabled ? '已啟用' : '已停用' }}</span>
                                        </label>
                                    </div>
                                    <p class="text-xs mt-1" style="color:var(--gray-500);">啟用後，CBI 會嘗試抓取寄件者網域與內文 URL 的少量頁面，產生 evidence 片段。</p>
                                </div>

                                <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <div>
                                        <label class="block text-sm font-bold mb-2" style="color:var(--gray-700);">每次分析最多抓取頁數</label>
                                        <div class="flex gap-2">
                                            <input v-model.number="aiSettingsForm.cbi_crawl_max_pages" type="number" min="0" step="1"
                                                class="flex-1 border p-2 rounded" />
                                            <button @click="saveAiSetting('cbi_crawl_max_pages', aiSettingsForm.cbi_crawl_max_pages)"
                                                class="btn btn-success">儲存</button>
                                        </div>
                                    </div>

                                    <div>
                                        <label class="block text-sm font-bold mb-2" style="color:var(--gray-700);">單頁超時秒數</label>
                                        <div class="flex gap-2">
                                            <input v-model.number="aiSettingsForm.cbi_crawl_timeout_seconds" type="number" min="1" step="1"
                                                class="flex-1 border p-2 rounded" />
                                            <button @click="saveAiSetting('cbi_crawl_timeout_seconds', aiSettingsForm.cbi_crawl_timeout_seconds)"
                                                class="btn btn-success">儲存</button>
                                        </div>
                                    </div>
                                </div>

                                <div class="mt-4">
                                    <label class="block text-sm font-bold mb-2" style="color:var(--gray-700);">根網域允許抓取的路徑（逗號分隔）</label>
                                    <div class="flex gap-2">
                                        <input v-model="aiSettingsForm.cbi_crawl_allowed_paths" type="text"
                                            class="flex-1 border p-2 rounded"
                                            placeholder="/,/about,/products,/solutions,/contact" />
                                        <button @click="saveAiSetting('cbi_crawl_allowed_paths', aiSettingsForm.cbi_crawl_allowed_paths)"
                                            class="btn btn-success">儲存</button>
                                    </div>
                                </div>

                                <div class="mt-4">
                                    <label class="block text-sm font-bold mb-2" style="color:var(--gray-700);">User-Agent</label>
                                    <div class="flex gap-2">
                                        <input v-model="aiSettingsForm.cbi_crawl_user_agent" type="text"
                                            class="flex-1 border p-2 rounded" placeholder="ai-mail-cbi/1.0" />
                                        <button @click="saveAiSetting('cbi_crawl_user_agent', aiSettingsForm.cbi_crawl_user_agent)"
                                            class="btn btn-success">儲存</button>
                                    </div>
                                    <p class="text-xs mt-1" style="color:var(--gray-500);">安全限制：僅抓取 http/https，拒絕 localhost 與內網 IP。</p>
                                </div>
                            </div>
                            <table class="data-table">
                                <thead>
                                    <tr>
                                        <th class="border p-2 text-left">設定項 (Key)</th>
                                        <th class="border p-2 text-left">值 (Value)</th>
                                        <th class="border p-2 text-left">說明</th>
                                        <th class="border p-2 w-20">操作</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr v-for="setting in aiSettings" :key="setting.setting_key">
                                        <td class="border p-2 font-mono text-sm">{{ setting.setting_key }}</td>
                                        <td class="border p-2">
                                            <input v-if="editingAiSetting === setting.setting_key"
                                                v-model="setting.setting_value"
                                                :type="isSensitiveSettingKey(setting.setting_key) ? 'password' : 'text'"
                                                class="border p-1 w-full rounded">
                                            <span v-else>{{ maskSettingValue(setting.setting_key, setting.setting_value) }}</span>
                                        </td>
                                        <td class="border p-2 text-sm" style="color:var(--gray-600);">{{ setting.description }}</td>
                                        <td class="border p-2 text-center">
                                            <button v-if="editingAiSetting === setting.setting_key"
                                                @click="saveAiSetting(setting.setting_key, setting.setting_value)"
                                                class="hover:underline" style="color:var(--success);">儲存</button>
                                            <button v-else @click="editingAiSetting = setting.setting_key"
                                                class="hover:underline" style="color:var(--primary);">編輯</button>
                                        </td>
                                    </tr>
                                </tbody>
                            </table>
                            <p class="text-sm" style="color:var(--gray-500);">這些設定會影響 AI 服務對垃圾郵件的判斷邏輯。</p>
                        </div>
                    </div>
                </div>

                <!-- Server: DNS Service -->

</template>

<script setup>
import { storeToRefs } from 'pinia'
import { useAppStore } from '../store/app.js'

const store = useAppStore()

// Spread all store state as reactive refs accessible in template
const stateRefs = storeToRefs(store)
// Destructure - Vue compiler exposes all variables declared in <script setup> to template
const {
  // Core state
  token, currentView, currentUser, loginForm, loginError,
  branding, notifications, showNotifPanel,
  // Command palette
  showCmdPalette, cmdQuery, cmdResults, cmdGrouped, cmdDefaults, cmdSelIdx,
  // Domain / Users / Departments
  domains, domainForm, showDomainForm, domainLoading,
  users, userForm, showUserForm, userLoading, userError,
  departments, deptForm, showDeptForm, deptLoading,
  // Contacts / Groups
  contacts, contactForm, showContactForm, contactLoading,
  groups, groupForm, showGroupForm, groupLoading,
  // Mail
  mailQueues, mailQueueLoading, mailQueueFilter,
  serviceStatus, serviceLoading,
  // Invoice Drive
  invoiceDriveState,
  // Quotes / Products
  quotes, quoteForm, showQuoteForm, quoteLoading,
  products, productForm, showProductForm, productLoading,
  priceBooks, priceBookForm, showPriceBookForm,
  // SSL
  sslCerts, sslLoading, sslDomain,
  // Security
  securityWizardStep, spamStats,
  securityFilterState,
  // Server settings
  serverMailSettings, serverDnsSettings, serverDbSettings, serverAiSettings,
  backupSettings,
  // Logs / Audit
  logs, logFilter, logLoading,
  mailAuditRecords, auditFilter, auditLoading,
  securityAuditLogs, securityAuditFilter,
  logAnalysisResult, logAnalysisLoading,
  // AI / RAG / Sales
  ragState, aiSalesState, aiMarketingState,
  // LINE
  lineSupportState,
  // Webmail
  webmailState, webmailAutoScanInvoices, webmailAutoScanMarkRead,
  // Unreplied
  unrepliedEmails, unrepliedLoading,
  // CBI
  cbiInsights, cbiLoading,
  // Misc
  sidebarCollapsed, sidebarMobile,
  journeyEnrollments, journeyEnrollmentsLoading,
  journeyFunnel, journeyFunnelLoading, journeyFunnelFilters,
  showJourneyEditorModal, showJourneyFunnelModal, showJourneyEnrollmentsModal,
  selectedJourneyForEnrollments, selectedJourneyForFunnel,
  journeyEditorForm,
  mailConfigTab, isSuperAdmin, mailSettings, dovecotSettings, dbStats,
  aiSettings, editingAiSetting, aiSettingsForm,
} = stateRefs

// Methods are accessed directly from store (not reactive, no storeToRefs needed)
const {
  login, logout, switchSidebarView, canAccess,
  openCommandPalette, closeCmdPalette, updateCmdResults, execCmdItemById,
  moveCmdSel, toggleNotifPanel, markNotifRead, clearNotifs,
  fetchDomains, saveDomain, deleteDomain,
  fetchUsers, saveUser, deleteUser, resetUserPassword,
  fetchDepartments, saveDepartment, deleteDepartment,
  fetchContacts, saveContact, deleteContact, importContacts,
  fetchGroups, saveGroup, deleteGroup,
  fetchMailQueue, releaseMailQueueItem, deleteMailQueueItem, flushMailQueue,
  fetchServiceStatus, fetchSpamStats,
  fetchSslCerts, issueSslCert, deleteSslCert,
  fetchProducts, saveProduct, deleteProduct,
  fetchPriceBooks, savePriceBook, deletePriceBook,
  fetchQuotes, saveQuote, deleteQuote, sendQuote,
  fetchLogs, fetchLogAnalysis,
  fetchMailAuditRecords, fetchSecurityAuditLogs,
  fetchServerMailSettings, saveServerMailSettings,
  fetchServerDnsSettings, saveServerDnsSettings,
  fetchServerDbSettings, saveServerDbSettings,
  fetchServerAiSettings, saveServerAiSettings,
  fetchBackupSettings, saveBackupSettings, triggerBackup,
  fetchSecurityFilter, saveSecurityFilter,
  fetchRagDocuments, uploadRagDocument, deleteRagDocument, queryRag,
  fetchUnreplied,
  fetchCbiInsights,
  loadBrandingFromLocal, saveBranding,
  safeArr, keyById,
  invoiceDriveCanGotoAdjacent, invoiceDriveGotoAdjacent,
  openInvoiceDetail, closeInvoiceDetail,
  controlService, fetchMailSettings, fetchDovecotSettings, fetchDbStats,
  fetchAiSettings, saveMailSettings, saveDovecotSettings, saveAiSetting,
  isSensitiveSettingKey, maskSettingValue,
} = store
</script>

